import { showToast } from 'components/toast'

interface MutationResult {
	error?: { status?: unknown; data?: unknown }
}

/**
 * Shows a toast for a failed RTK-Query mutation result (`{ error: ... }`).
 * Returns true if an error was shown (callers should stop/return right
 * after), false if the result succeeded.
 *
 * A network failure (FETCH_ERROR/TIMEOUT_ERROR) always gets the same
 * connectivity message regardless of `fallbackMessage` -- err-12: "Something
 * went wrong" is actively misleading when the real problem is the user's
 * own connection, not the server.
 *
 * Consolidates 7 of the 10 real `if ('error' in result)` sites found across
 * web/src (AUDIT-2026-09-23-lego-rewrite.md's RTK-Query error-handling
 * item): 6 were byte-identical, 1 (signup.js) had the exact same
 * network-check-then-fallback shape. The other 3 are deliberately NOT
 * routed through this helper -- they're genuinely different, not
 * boilerplate: login.js has 5 distinct branches (Cognito challenge
 * redirect, rate-limit, 5xx, default) and calls `setError`, not
 * `showToast`; new-password-required.tsx extracts the server's own
 * `error.data.message` instead of using a fixed fallback.
 */
export const showMutationError = (result: MutationResult, fallbackMessage = 'Something went wrong. Please try again.'): boolean => {
	if (!('error' in result)) return false

	const { status } = result.error ?? {}
	if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
		showToast("We couldn't reach the server. Check your internet connection and try again.")
		return true
	}

	showToast(fallbackMessage)
	return true
}
